import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import {
  IParameterDetailsProp,
  IPartnerProps,
  IKeyPartnersProps,
} from '../TnnCampaignCreationStepper';
import { SponsorFormState } from '../task/cmpt/AddSponsorStep';
import useDebounce from '../../../../components/utlis/useDebounce';
import { CustomTextarea } from '../../../../components/styles/CustomTextarea';
import { apiPut } from '../../../../axiosConfig';
import { IApiSuccessMsgResponse } from '../../../../dto/API.dto';
import { ICompany } from '../../../../dto/Company.dto';
import { TnnReviewSummary } from './TnnReviewSummary';

export const ReviewCmpt = ({
  campaignName,
  adSource,
  campaignType,
  commercialLength,
  type,
  parameter,
  partners,
  setPartners,
  sponsorForm,
  isReview = true,
  notes,
  sponsorRequestId,
  scheduleId,
  campaignReviewLayout = false,
  hubspotLayout = false,
}: {
  campaignName: string;
  adSource: string;
  type: string;
  commercialLength: string;
  campaignType: string[];
  parameter: IParameterDetailsProp;
  partners: IPartnerProps;
  setPartners?: React.Dispatch<React.SetStateAction<IPartnerProps>>;
  sponsorForm?: SponsorFormState;
  isReview?: boolean;
  notes?: string;
  sponsorRequestId?: number;
  scheduleId?: number;
  campaignReviewLayout?: boolean;
  hubspotLayout?: boolean;
}) => {
  const notesField =
    notes !== undefined && sponsorRequestId && scheduleId ? (
      <DebouncedTextField
        notes={notes}
        url={`v1/tnn/sponsor/${sponsorRequestId}/campaign/${scheduleId}/notes`}
      />
    ) : null;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <TnnReviewSummary
        sponsorForm={sponsorForm}
        campaignName={campaignName}
        adSource={adSource}
        commercialLength={commercialLength}
        type={type}
        campaignType={campaignType}
        parameter={parameter}
        partners={partners}
        setPartners={setPartners}
        isReview={isReview}
        campaignReviewLayout={campaignReviewLayout}
        firstColumnContent={campaignReviewLayout ? notesField : undefined}
        hubspotLayout={hubspotLayout}
      />
      {!campaignReviewLayout && notesField ? (
        <Box sx={{ mt: 2 }}>{notesField}</Box>
      ) : null}
    </Box>
  );
};

export const CompanyListCmpt = ({
  title,
  companies,
}: {
  title: string;
  companies: ICompany[];
}) => (
  <Box sx={{ width: '100%', maxWidth: 360 }}>
    <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
      {`${title} (${companies.length})`}
    </Typography>
    <List
      className='scrollbar'
      sx={{
        maxHeight: 275,
        overflow: 'auto',
        backgroundColor: 'background.default',
        borderRadius: 1,
        p: 1,
      }}>
      {companies.map(company => (
        <ListItemButton key={company.id} dense divider>
          <ListItemText primary={company.name} />
        </ListItemButton>
      ))}
    </List>
  </Box>
);

export const DebouncedTextField = ({
  notes,
  url,
}: {
  notes: string;
  url: string;
}) => {
  const [newNotes, setNewNotes] = useState(notes);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedText = useDebounce(newNotes, 1000);

  const handleSubmit = useCallback(() => {
    apiPut(url, {
      notes: debouncedText,
    })
      .then((_response: AxiosResponse<IApiSuccessMsgResponse>) => {
        setIsLoading(false);
      })
      .catch(_error => {
        setIsLoading(false);
      });
  }, [url, debouncedText]);

  useEffect(() => {
    if (debouncedText === notes) return;
    const runApi = async () => {
      setIsLoading(true);
      await handleSubmit();
    };

    runApi();
  }, [debouncedText, handleSubmit, notes]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewNotes(e.target.value);
  }, []);

  return (
    <Box position='relative' width='100%' height='375px'>
      <CustomTextarea
        id='message-description'
        placeholder='Enter Note here'
        onChange={handleChange}
        value={newNotes}
        sx={{
          height: '100%',
          m: 0,
          p: 0,
          '& .MuiInputBase-root': {
            height: '100%',
            alignItems: 'stretch',
            backgroundColor: 'transparent',
          },
        }}
      />
      {isLoading ? (
        <Box
          position='absolute'
          bottom={8}
          right={8}
          display='flex'
          alignItems='center'
          justifyContent='center'>
          <span className='text-white-50'>Saving…</span>
        </Box>
      ) : null}
    </Box>
  );
};

export type { IKeyPartnersProps };
